import { useRef, useState } from "react"
import { ArrowRight, Camera01, Car01, Keyboard01 } from "@untitledui/icons"
import { publicConsumerApi } from "@/lib/api/public-consumer"
import { isVinValid } from "@/lib/vin/vin-validator"
import { AInput, ASelect } from "@/components/onboarding-activation/ui"
import { cx } from "@/lib/utils/cx"
import { FlowCta, FlowHeading, IntakeField, PhasePane, QuietLink } from "./flow-chrome"
import type { IntakeDevice } from "./intake-flow-types"
import type { IntakeFlowVehicle } from "./use-intake-flow-state"
import { CONSUMER_V2 } from "./copy"
import { makeName } from "./capture/vin/use-vin-step"

// "Which car is it?" — three ways in, all skippable (unless the shop requires
// a vehicle): snap the VIN sticker (photo → OCR → decode → one-tap confirm),
// type the VIN or plate, or pick year/make/model. Continue is always enabled;
// leaving everything empty just skips.

const C = CONSUMER_V2.extras.vehicle

const YEARS = Array.from({ length: new Date().getFullYear() + 2 - 1990 }, (_, i) =>
  String(new Date().getFullYear() + 1 - i),
)

const COMMON_MAKES = [
  "Toyota",
  "Honda",
  "Ford",
  "Chevrolet",
  "Nissan",
  "Jeep",
  "Hyundai",
  "Kia",
  "Subaru",
  "GMC",
  "Ram",
  "BMW",
  "Mercedes-Benz",
  "Volkswagen",
  "Tesla",
  "Lexus",
  "Mazda",
  "Dodge",
  "Audi",
  "Cadillac",
  "Buick",
  "Chrysler",
  "Acura",
  "Volvo",
  "Lincoln",
  "Infiniti",
]

type Mode = "tiles" | "snap-confirm" | "type" | "ymm"

function vehicleLabel(v: IntakeFlowVehicle): string {
  return [v.year, v.make, v.model].filter(Boolean).join(" ")
}

export function StepVehicleLite({
  device,
  vehicle,
  setVehicle,
  required,
  onDone,
  onSkip,
  onRejectFound,
}: {
  device: IntakeDevice
  vehicle: IntakeFlowVehicle
  setVehicle: (patch: Partial<IntakeFlowVehicle>) => void
  required: boolean
  onDone: (vehicle: IntakeFlowVehicle) => void
  onSkip: () => void
  // "No, that's not it": clears what the sticker photo filled, here and on the lead.
  onRejectFound: () => void
}) {
  // Only asked when the camera's VIN step was skipped: a car with a VIN
  // never gets here.
  const [mode, setMode] = useState<Mode>("tiles")
  const [reading, setReading] = useState(false)
  const [snapError, setSnapError] = useState<string | null>(null)
  const [decoding, setDecoding] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const hasVehicle =
    isVinValid(vehicle.vin) || vehicle.plate.trim().length > 0 || vehicleLabel(vehicle).length > 0

  const handleSnapFile = async (file: File) => {
    setReading(true)
    setSnapError(null)
    try {
      const { vin } = await publicConsumerApi.vinFromImage(file)
      const decoded = await publicConsumerApi.decodeVin(vin)
      setVehicle({
        vin: decoded.vin,
        year: decoded.year ?? "",
        make: makeName(decoded.make),
        model: decoded.model ?? "",
        trim: decoded.trim ?? "",
        type: decoded.type ?? null,
        decodedFrom: "vin-photo",
      })
      setMode("snap-confirm")
    } catch {
      // OCR miss, decode miss, network — one message, one recovery path.
      setSnapError(C.readFailed)
      setMode("type")
    } finally {
      setReading(false)
    }
  }

  // Typed VIN: decode when it checks out so the shop gets Y/M/M too; a failed
  // decode still keeps the raw VIN (the shop can run it). The final vehicle
  // travels WITH onDone so the orchestrator never patches from stale state.
  const handleContinue = async () => {
    if (decoding) return
    const vin = vehicle.vin.trim().toUpperCase()
    let next: IntakeFlowVehicle = vehicle
    if (mode === "type" && isVinValid(vin) && vehicle.decodedFrom !== "vin-photo") {
      setDecoding(true)
      try {
        const decoded = await publicConsumerApi.decodeVin(vin)
        next = {
          ...vehicle,
          vin: decoded.vin,
          year: decoded.year ?? "",
          make: makeName(decoded.make),
          model: decoded.model ?? "",
          trim: decoded.trim ?? "",
          type: decoded.type ?? null,
          decodedFrom: "manual",
        }
      } catch {
        next = { ...vehicle, vin, decodedFrom: "manual" }
      } finally {
        setDecoding(false)
      }
    } else if (mode === "type" && vehicle.plate.trim() && !isVinValid(vin)) {
      next = { ...vehicle, decodedFrom: "plate" }
    } else if (mode === "ymm" && vehicleLabel(vehicle)) {
      next = { ...vehicle, decodedFrom: "manual" }
    }
    if (next !== vehicle) setVehicle(next)
    onDone(next)
  }

  return (
    <PhasePane>
      <header className="mt-5">
        <FlowHeading className="text-[28px] font-bold leading-[1.15] tracking-tight text-ink">
          {C.title}
        </FlowHeading>
        <p className="mt-2 text-[15px] leading-relaxed text-zinc-600">{required ? C.required : C.sub}</p>
      </header>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void handleSnapFile(f)
          e.target.value = ""
        }}
      />

      <div className="mt-5 flex-1 lg:pointer-fine:flex-none space-y-4">
        {mode === "tiles" && (
          <div className="space-y-2.5">
            {device === "phone" && (
              <VehicleTile
                icon={<Camera01 className="size-5" aria-hidden />}
                title={C.snapTile}
                sub={C.snapSub}
                busy={reading}
                busyLabel={C.reading}
                onClick={() => fileRef.current?.click()}
              />
            )}
            <VehicleTile
              icon={<Keyboard01 className="size-5" aria-hidden />}
              title={C.typeTile}
              onClick={() => setMode("type")}
            />
            <VehicleTile
              icon={<Car01 className="size-5" aria-hidden />}
              title={C.pickTile}
              onClick={() => setMode("ymm")}
            />
          </div>
        )}

        {mode === "snap-confirm" && (
          <div className="rounded-2xl bg-signal/8 p-5">
            <p className="text-[18px] font-bold text-ink">
              {C.confirm(vehicleLabel(vehicle) || vehicle.vin)}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => onDone(vehicle)}
                className="h-12 rounded-xl bg-ink text-[15px] font-semibold text-white hover:bg-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
              >
                {C.confirmYes}
              </button>
              <button
                type="button"
                onClick={() => {
                  onRejectFound()
                  setMode("type")
                }}
                className="h-12 rounded-xl bg-white text-[15px] font-semibold text-ink ring-1 ring-ink/12 hover:ring-ink/25 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
              >
                {C.confirmNo}
              </button>
            </div>
          </div>
        )}

        {mode === "type" && (
          <div className="space-y-4">
            {snapError && (
              <p
                role="alert"
                className="rounded-xl bg-amber-50 px-4 py-3 text-[14px] font-medium text-amber-900"
              >
                {snapError}
              </p>
            )}
            <IntakeField label={C.vinLabel} hint={C.vinHint}>
              {(control) => (
                <AInput
                  {...control}
                  value={vehicle.vin}
                  onChange={(e) => setVehicle({ vin: e.target.value.toUpperCase() })}
                  placeholder="1HGCV1F34MA000000"
                  maxLength={17}
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  valid={isVinValid(vehicle.vin.trim().toUpperCase())}
                  className="h-13 rounded-xl font-mono text-[17px] tracking-wide"
                />
              )}
            </IntakeField>
            <IntakeField label={C.plateLabel} hint={C.plateHint}>
              {(control) => (
                <AInput
                  {...control}
                  value={vehicle.plate}
                  onChange={(e) => setVehicle({ plate: e.target.value.toUpperCase() })}
                  placeholder="ABC 1234"
                  maxLength={12}
                  autoCapitalize="characters"
                  autoComplete="off"
                  className="h-13 rounded-xl text-[17px]"
                />
              )}
            </IntakeField>
          </div>
        )}

        {mode === "ymm" && (
          <div className="space-y-4">
            <IntakeField label={C.yearLabel}>
              {(control) => (
                <div role="group" aria-labelledby={`${control.id}-label`}>
                  <ASelect
                    value={vehicle.year}
                    onChange={(year) => setVehicle({ year })}
                    options={YEARS}
                    placeholder="Pick a year"
                  />
                </div>
              )}
            </IntakeField>
            <IntakeField label={C.makeLabel}>
              {(control) => (
                <>
                  <AInput
                    {...control}
                    list="intake-makes"
                    value={vehicle.make}
                    onChange={(e) => setVehicle({ make: e.target.value })}
                    placeholder="Toyota"
                    maxLength={40}
                    autoComplete="off"
                    className="h-13 rounded-xl text-[17px]"
                  />
                  <datalist id="intake-makes">
                    {COMMON_MAKES.map((m) => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                </>
              )}
            </IntakeField>
            <IntakeField label={C.modelLabel}>
              {(control) => (
                <AInput
                  {...control}
                  value={vehicle.model}
                  onChange={(e) => setVehicle({ model: e.target.value })}
                  placeholder="Camry"
                  maxLength={60}
                  autoComplete="off"
                  className="h-13 rounded-xl text-[17px]"
                />
              )}
            </IntakeField>
          </div>
        )}
      </div>

      {mode !== "snap-confirm" && (
        <div className="mt-6 space-y-2">
          <FlowCta
            onClick={() => void handleContinue()}
            loading={decoding}
            disabled={required && !hasVehicle}
          >
            {CONSUMER_V2.extras.continue}
            <ArrowRight className="size-4" aria-hidden />
          </FlowCta>
          {!required && <QuietLink onClick={onSkip}>{CONSUMER_V2.extras.skip}</QuietLink>}
        </div>
      )}
    </PhasePane>
  )
}

function VehicleTile({
  icon,
  title,
  sub,
  busy,
  busyLabel,
  onClick,
}: {
  icon: React.ReactNode
  title: string
  sub?: string
  busy?: boolean
  busyLabel?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={cx(
        "flex w-full items-center gap-3.5 rounded-2xl bg-white p-4 text-left ring-1 ring-ink/10 transition-colors duration-150",
        "hover:ring-ink/25 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:opacity-70",
      )}
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-ink/5 text-ink">
        {busy ? (
          <span className="size-4 animate-spin rounded-full border-2 border-ink/15 border-t-ink" />
        ) : (
          icon
        )}
      </span>
      <span className="min-w-0">
        <span className="block text-[16px] font-semibold leading-tight text-ink">
          {busy && busyLabel ? busyLabel : title}
        </span>
        {sub && !busy && <span className="mt-1 block text-[14px] leading-snug text-zinc-600">{sub}</span>}
      </span>
    </button>
  )
}
